import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Books, ArrowRight, ArrowLeft, MagnifyingGlass, CaretRight, WarningCircle, ArrowClockwise } from '@phosphor-icons/react';
import {
  listMyClassroomsPaginated,
  type ClassroomSummary,
} from '../lib/direction';

const PAGE_SIZE = 8;

export default function DirectionMyClasses() {
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState<ClassroomSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    listMyClassroomsPaginated(0, 200)
      .then((res) => { setClassrooms(res.classrooms); setTotal(res.total); })
      .catch(() => { setClassrooms([]); setTotal(0); setFailed(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const levels = [...new Set(classrooms.map((c) => c.level).filter(Boolean))] as string[];

  const filtered = classrooms.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.primary_teacher_name ?? '').toLowerCase().includes(q);
    const matchLevel = !levelFilter || c.level === levelFilter;
    return matchSearch && matchLevel;
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(0); };
  const handleLevel = (l: string | null) => { setLevelFilter(l); setPage(0); };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<CaretRight size={11} weight="bold" />}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Mes classes</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Votre périmètre est déterminé par les groupes de classes qui vous sont confiés.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px] max-w-[300px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]" size={14} />
            <input
              type="search"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Rechercher une classe…"
              className="w-full pl-8 pr-3 py-[7px] rounded-[8px] border border-[var(--naia-line)] bg-[var(--naia-card)] text-[13px] text-[var(--naia-text-primary)] outline-none focus:border-primary transition-colors font-[inherit]"
            />
          </div>
          {levels.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => handleLevel(levelFilter === l ? null : l)}
              className={cn(
                "px-[11px] py-[5px] rounded-full border text-[12px] font-bold cursor-pointer transition-colors font-[inherit]",
                levelFilter === l
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-[var(--naia-line)] bg-[var(--naia-card)] text-[var(--naia-text-secondary)] hover:border-primary"
              )}
            >
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13.5px] text-[var(--naia-text-primary)]">
              <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>Vos classes n'ont pas pu être chargées. Votre périmètre est intact.</span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={load}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucune classe dans votre périmètre.</p>
        ) : (
          <>
            <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]">
              {pageItems.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/direction/mes-classes/${c.id}`)}
                  className="flex items-center gap-3 w-full px-4 py-[13px] bg-transparent border-none cursor-pointer text-left font-[inherit] hover:bg-accent transition-colors"
                >
                  <div className="shrink-0 w-9 h-9 rounded-[8px] bg-primary/10 flex items-center justify-center">
                    <Books size={17} weight="fill" className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-[14.5px] text-[var(--naia-text-primary)]">{c.name}</span>
                      {c.level && (
                        <span className="text-[10.5px] font-extrabold text-[var(--naia-text-secondary)] uppercase tracking-[0.5px]">{c.level}</span>
                      )}
                      {c.school_year && (
                        <span className="text-[10.5px] font-semibold text-[var(--naia-text-secondary)] tabular-nums">{c.school_year}</span>
                      )}
                    </div>
                    <div className="text-[12px] text-[var(--naia-text-secondary)] mt-[1px] truncate">
                      {c.primary_teacher_name ? c.primary_teacher_name : 'Aucun enseignant'}
                      {' · '}
                      <span className="font-semibold text-primary">{c.active_count} actif{c.active_count !== 1 ? 's' : ''}</span>
                      {' / '}{c.total_count} élève{c.total_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-[var(--naia-text-secondary)] shrink-0" />
                </button>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="gap-1.5"
                >
                  <ArrowLeft size={13} /> Précédent
                </Button>
                <span className="text-[12.5px] text-[var(--naia-text-secondary)] font-semibold tabular-nums">
                  Page {safePage + 1} / {pageCount} · {filtered.length} classe{filtered.length !== 1 ? 's' : ''}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="gap-1.5"
                >
                  Suivant <ArrowRight size={13} />
                </Button>
              </div>
            )}

            {pageCount === 1 && (
              <p className="text-[12px] text-[var(--naia-text-secondary)] mt-3 text-right tabular-nums">
                {filtered.length} classe{filtered.length !== 1 ? 's' : ''}
                {total !== filtered.length ? ` (${total} au total)` : ''}
              </p>
            )}
          </>
        )}
      </main>
    </>
  );
}
